import { useTranslation } from "react-i18next";

import type {
  JackpotView,
  JackpotWinRow,
} from "@/campaigns/modules/jackpot/types";
import {
  ceilingFraction,
  jackpotCardState,
  jackpotKind,
  msToDeadline,
  potDisplay,
  timedTier,
} from "@/campaigns/modules/jackpot/types";
import { FINISHED_DIM } from "@/components/campaigns/campaign-shell/campaign-view";
import type { PublicCampaignSchema } from "@/types";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { relativeTimeParts } from "@/utils/relative-time";

import { JackpotBoostBadge } from "./jackpot-boost-badge";
import { JackpotCeilingScale } from "./jackpot-ceiling-scale";
import { JackpotPot, type JackpotCardSize } from "./jackpot-pot";
import { JackpotTimePill } from "./jackpot-time-pill";

/**
 * One jackpot. The operator's name on top, the pot dead centre with its kind's one secondary
 * element under it (the ceiling scale for pays-before, the time pill for timed, nothing for a
 * progressive) and, when the pot has one, its boost window (`JackpotBoostBadge`), the last win
 * in the foot. Two sizes, chosen by how many jackpots share the
 * board; the card hugs its content and never stretches to fill.
 *
 * The state (`jackpotCardState`) changes the secondary element and how the card is coloured:
 * `justWon` names the winner for two minutes and explains why the pot fell back to its base,
 * `mustWin` (timed only, deadline passed and still unpaid) takes the urgent chrome and says the
 * pot wins on the next spin, `cooldown` (timed only) counts to the next round in grey, `finished` dims everything but the
 * title row. Only the final minute of a timed pot touches the card's own chrome (an urgent
 * border and glow) — every other state colours the elements inside.
 *
 * `now` comes from the board's `useJackpotClock`; nothing here reads the clock itself.
 */
/** The dense tile's corner pill: a step under the compact size, so it labels the pot rather
 *  than competing with it. */
const DENSE_PILL =
  "shrink-0 gap-1.5 px-2 py-0.5 text-[11px] leading-[14px] [&_svg]:size-3";

export const JackpotCard = ({
  view,
  campaign,
  size,
  now,
  latestWin = null,
  dense = false,
  className,
}: {
  view: JackpotView;
  campaign: PublicCampaignSchema;
  size: JackpotCardSize;
  now: number;
  /** The newest feed row for THIS jackpot, if any — names the winner in the `justWon` state. */
  latestWin?: JackpotWinRow | null;
  /** A tile in the landscape grid (~120px tall): the time pill moves up into the title row's
   *  corner at a smaller size, the last-win foot drops (the latest-wins column beside the grid
   *  says it), and the pot sits centred in what is left — so the amount is always whole. */
  dense?: boolean;
  className?: string;
}) => {
  const { t } = useTranslation();
  const state = jackpotCardState(view, campaign, now);
  const kind = jackpotKind(view.jackpot);
  const compact = size === "compact";
  const finished = state === "finished";
  const live = state === "live";

  const pot = potDisplay(view);
  const hasCeiling = ceilingFraction(view) !== null;
  const deadline = live && kind === "TD" ? msToDeadline(view, now) : null;
  const finalMinute = deadline !== null && timedTier(deadline) === "final";
  const mustWin = state === "mustWin";
  const urgentChrome = finalMinute || mustWin;

  const last = view.events?.last ?? null;
  const lastWinLine =
    last?.payout_date && last.payout_amount.display
      ? `${last.payout_amount.display} · ${relative(last.payout_date, now, t)}`
      : null;

  // The timed kinds' one secondary element: a countdown, the cooldown, or "Must Drop Now". Under
  // the pot on the stacked cards; in the title row's corner on a dense tile.
  const timePill = (className?: string) =>
    live && kind === "TD" && !hasCeiling ? (
      <JackpotTimePill
        view={view}
        now={now}
        compact={compact}
        className={className}
      />
    ) : state === "cooldown" ? (
      <JackpotTimePill
        view={view}
        now={now}
        cooldown
        compact={compact}
        className={className}
      />
    ) : mustWin ? (
      <JackpotTimePill
        view={view}
        now={now}
        mustWin
        compact={compact}
        className={className}
      />
    ) : null;

  return (
    <Card
      inset="none"
      urgent={urgentChrome}
      data-state={state}
      data-kind={kind ?? "unknown"}
      className={cn(
        // The shared card (its `overflow-hidden` is where the meter's comet glow stops at low
        // values); urgency lights the fill and rim in `--urgent`. Own paddings: the pot
        // amount needs more air than a text card.
        "flex flex-col",
        dense
          ? "gap-1 px-3.5 pt-3 pb-3"
          : compact
            ? "gap-2.5 px-4 pt-[14px] pb-3 lg:px-6"
            : "gap-6 px-4 pt-4 pb-4 lg:gap-8 lg:px-6 lg:pt-5 lg:pb-[18px]",
        className
      )}
    >
      {/* top: the operator's own name for this pot. No kind or state chip — the secondary element
          under the pot says what kind it is and where it stands, and the title stands alone. */}
      {/* The row wraps: in a half-width dense tile the corner pill drops under the title rather
          than crushing it to a letter a line (the title keeps a minimum measure); the pill then
          sits at the row's end on its own line. */}
      <div className="flex w-full flex-wrap items-center justify-between gap-x-3 gap-y-1">
        {/* Same small uppercase label as every other card heading ("How it works", "Prizes"), so
            the pot title frames the amount instead of competing with it. Text tokens (not muted):
            the title is the operator's name for the pot, so it stays a notch lighter. */}
        <h3
          className={cn(
            "min-w-24 flex-1 font-heading text-[12px] leading-4 font-semibold tracking-[0.88px] break-words uppercase lg:text-[11px] lg:leading-[14px]",
            finished ? "text-muted-foreground" : "text-foreground-secondary"
          )}
        >
          {view.jackpot.title}
        </h3>
        {dense && timePill(cn(DENSE_PILL, "ml-auto"))}
      </div>

      {/* centre: the pot, and this kind's one secondary element under it */}
      <div
        className={cn(
          "flex w-full flex-col items-center",
          compact ? "gap-2" : "gap-3",
          // The pot is the hero: it fills the tile's spare height but is never the element that
          // gives height up (a tall title row clipped it out of the card entirely).
          dense && "shrink-0 grow justify-center gap-1.5",
          finished && FINISHED_DIM
        )}
      >
        {/* Announced, not silently swapped: the pot moves under the player without any action
            of theirs. `polite` so it never interrupts a screen-reader user mid-sentence. */}
        {/* The hero gets air above and below: the number must never feel wedged between the
            title row and its secondary element. */}
        {/* `max-w-full`: centred, the wrapper would hug the pot's natural width, and the pot
            could never see a narrower box to shrink into (`JackpotPot`'s fit). */}
        <div
          role="heading"
          aria-level={4}
          aria-live="polite"
          className={cn(
            "max-w-full",
            dense ? "py-0" : compact ? "py-1.5" : "py-2"
          )}
        >
          {pot ? (
            <JackpotPot display={pot} size={size} muted={finished} />
          ) : (
            // Before the progress stream has pushed: hold the height, say nothing — "€0" would
            // be the one thing this card must never claim.
            <span
              aria-hidden
              className={cn(
                "block rounded-md bg-foreground/[0.06]",
                compact ? "h-[45px] w-32" : "h-[58px] w-52"
              )}
            />
          )}
        </div>

        {state === "justWon" && (
          <p className="font-heading text-[13px] leading-[18px] font-bold text-active lg:text-sm">
            {latestWin &&
            latestWin.player !== null &&
            last?.payout_amount.display
              ? t("jackpot.justWonBy", "{{name}} just won {{amount}}", {
                  name: latestWin.name,
                  amount: last.payout_amount.display,
                })
              : t("jackpot.justWonAmount", "{{amount}} just won", {
                  amount: last?.payout_amount.display ?? "",
                })}
          </p>
        )}

        {live && hasCeiling && (
          <JackpotCeilingScale view={view} compact={compact} />
        )}
        {!dense && timePill()}
        {live && <JackpotBoostBadge view={view} now={now} />}
      </div>

      {/* foot: the last payout — the one history fact the wire carries */}
      {lastWinLine && !dense && (
        <div
          className={cn("flex w-full flex-col gap-2", finished && FINISHED_DIM)}
        >
          <div aria-hidden className="h-px w-full bg-foreground/[0.08]" />
          <div className="flex w-full items-center gap-1.5">
            <span className="font-heading text-[10px] leading-[13px] font-semibold tracking-[0.6px] text-muted-foreground uppercase">
              {state === "justWon"
                ? t("jackpot.justWon", "Just won")
                : t("jackpot.lastWin", "Last win")}
            </span>
            <span className="font-heading text-xs leading-4 font-medium text-foreground-secondary tabular-nums">
              {lastWinLine}
            </span>
          </div>
        </div>
      )}
    </Card>
  );
};

type Translate = (
  key: string,
  fallback: string,
  opts?: Record<string, unknown>
) => string;

/** "just now" / "11 min ago" / "3 h ago" / "2 d ago" — the same buckets and keys the feed uses. */
function relative(wonAt: string, now: number, t: Translate): string {
  const { key, count } = relativeTimeParts(wonAt, now);
  switch (key) {
    case "justNow":
      return t("sprintMission.justNow", "just now");
    case "minutesAgo":
      return t("sprintMission.minutesAgo", "{{count}} min ago", { count });
    case "hoursAgo":
      return t("sprintMission.hoursAgo", "{{count}} h ago", { count });
    default:
      return t("sprintMission.daysAgo", "{{count}} d ago", { count });
  }
}
